import { useEffect, useState, type FormEvent } from 'react';
import { ApiError } from '@/lib/apiClient';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { useLocation } from 'react-router-dom';
import { loginSchema, type LoginFormValues } from '@/auth/schemas';
import { useLogin } from '@/hooks/useAuthActions';
import { humanizeError } from '@/utils/errorMessage';
import { storage } from '@/lib/storage';
import { isTravelErpHost } from '@/hooks/useLoginPage';

/**
 * The company a sign-in page is dressed for. Null on the TravelERP address, where the page is TravelERP's
 * own; set on a company's verified domain, where its name, colour and logo replace TravelERP's.
 */
export interface LoginBrand {
  companyName: string;
  logoUrl: string | null;
  primaryColor: string;
}

/** The server's answer when the password is right and a code was emailed (not a failure). */
const OTP_REQUIRED = 'AUTH_OTP_REQUIRED';

/** What every sign-in design receives. */
export interface LoginTemplateProps {
  brand: LoginBrand | null;
  initialEmail?: string;
  /** Settings › Branding preview: the page as it will look, with signing in switched off. */
  preview?: boolean;
}

/**
 * Why they are back at the login page, when it was the app — not them — that ended the last session.
 * Without it a sign-out looks like the app losing the session on its own, which is the sort of thing
 * people stop trusting.
 */
function sessionNotice(state: unknown): string | null {
  const s = state as { idle?: boolean; ended?: 'logout' | 'expired' | null } | null;
  if (s?.idle) return 'You were signed out after 30 minutes of inactivity. Please sign in again.';
  if (s?.ended === 'expired') return 'Your session has expired. Please sign in again.';
  if (s?.ended === 'logout') return 'You were signed out in another tab. Please sign in again.';
  return null;
}

/**
 * The sign-in behaviour shared by every design — validation, Remember me, the auth call and its error —
 * so the designs differ only in how they look. Where a successful sign-in lands is the login page's one
 * rule (resolveLandingPath), shared with a session restored on load and a sign-in in another tab.
 */
export function useLoginForm(initialEmail = '', preview = false) {
  const location = useLocation();
  const login = useLogin();
  const notice = preview ? null : sessionNotice(location.state);

  const [error, setError] = useState<string | null>(null);
  const [showPwd, setShowPwd] = useState(false);
  // Decides WHERE the session is kept, not what is sent to the server. Ticked keeps it in
  // localStorage and it survives closing the browser; unticked keeps it in sessionStorage, which the
  // browser drops when the tab closes. Ticked on arrival.
  const [remember, setRemember] = useState(true);

  const form = useForm<LoginFormValues>({
    resolver: zodResolver(loginSchema),
    defaultValues: { email: initialEmail, password: '' },
  });

  // ---- Second step: the code emailed after the password is accepted ----
  const [creds, setCreds] = useState<{ email: string; password: string } | null>(null);
  const [otpMessage, setOtpMessage] = useState('');
  const [otpCode, setOtpCode] = useState('');
  const [otpError, setOtpError] = useState<string | null>(null);
  const [resendIn, setResendIn] = useState(0);
  useEffect(() => {
    if (resendIn <= 0) return;
    const t = window.setTimeout(() => setResendIn((s) => s - 1), 1000);
    return () => window.clearTimeout(t);
  }, [resendIn]);

  const host = window.location.hostname;
  const call = (email: string, password: string, extra: { otpCode?: string; resendOtp?: boolean } = {}) =>
    login.mutateAsync({ email, password, signInHost: isTravelErpHost(host) ? null : host, ...extra });

  /** The server's own words for code problems ("2 attempts left", "already used"), else the friendly text. */
  const otpText = (err: unknown) =>
    err instanceof ApiError && err.errorCode?.startsWith('OTP_') && err.message ? err.message : humanizeError(err, 'Could not sign in. Please try again.');

  // Email + Password only. Tenant/company + branch context are resolved server-side from the
  // authenticated identity; the user never enters a company/tenant/workspace code here.
  const onSubmit = form.handleSubmit(async (values) => {
    if (preview) return;
    setError(null);

    // Recorded before the session is written, because it decides which store receives it.
    storage.setRememberMe(remember);

    const email = values.email.trim().toLowerCase();
    try {
      await call(email, values.password);
      // Signed in. Where to land is decided by the login page (resolveLandingPath).
    } catch (err) {
      if (err instanceof ApiError && err.errorCode === OTP_REQUIRED) {
        // Password accepted: a code was emailed. The next screen asks for it.
        setCreds({ email, password: values.password });
        setOtpMessage(err.message);
        setOtpCode('');
        setOtpError(null);
        setResendIn(60);
        return;
      }
      setError(humanizeError(err, 'Could not sign in. Check your details and try again.'));
    }
  });

  const verifyOtp = async (e?: FormEvent) => {
    e?.preventDefault();
    if (!creds || preview) return;
    const code = otpCode.replace(/\D/g, '');
    if (code.length !== 6) { setOtpError('Enter the 6-digit code from the email.'); return; }
    setOtpError(null);
    try {
      await call(creds.email, creds.password, { otpCode: code });
      // Signed in: the login page moves on.
    } catch (err) {
      setOtpError(otpText(err));
    }
  };

  const resendOtp = async () => {
    if (!creds || resendIn > 0) return;
    setOtpError(null);
    try {
      await call(creds.email, creds.password, { resendOtp: true });
    } catch (err) {
      if (err instanceof ApiError && err.errorCode === OTP_REQUIRED) {
        setOtpMessage(err.message);
        setOtpCode('');
        setResendIn(60);
        return;
      }
      setOtpError(otpText(err));
    }
  };

  return {
    form,
    onSubmit,
    notice,
    error,
    busy: login.isPending,
    showPwd,
    toggleShowPwd: () => setShowPwd((v) => !v),
    remember,
    setRemember,
    otp: {
      /** True once the password is accepted and a code was emailed. */
      step: creds !== null,
      email: creds?.email ?? '',
      message: otpMessage,
      code: otpCode,
      setCode: (v: string) => { setOtpCode(v.replace(/\D/g, '').slice(0, 6)); setOtpError(null); },
      error: otpError,
      busy: login.isPending,
      resendIn,
      verify: verifyOtp,
      resend: resendOtp,
      /** Back to email and password (another account, or a typo in the email). */
      back: () => { setCreds(null); setOtpCode(''); setOtpError(null); },
    },
  };
}

/** The sign-in second step, as each design receives it. */
export type LoginOtpState = ReturnType<typeof useLoginForm>['otp'];
